/**
 * Theme Bundle - Optimized JavaScript for Voodoo Neon Theme
 * Combines common functionality for better performance
 */

// Core theme utilities
class ThemeUtils {
  static debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  }

  static throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  static loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  static loadCSS(href) {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }
}

// Enhanced cart functionality
class CartManager {
  constructor() {
    this.cart = null;
    this.isUpdating = false;
    this.observers = new Set();
  }

  async getCart() {
    if (!this.cart) {
      const response = await fetch(`${window.routes.cart_url}.js`);
      this.cart = await response.json();
    }
    return this.cart;
  }

  async addItem(variantId, quantity = 1, properties = {}) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity,
          properties
        })
      });

      if (!response.ok) throw new Error('Failed to add item');
      
      const item = await response.json();
      this.cart = null; // Invalidate cache
      this.notifyObservers('item-added', item);
      return item;
    } finally {
      this.isUpdating = false;
    }
  }

  addObserver(callback) {
    this.observers.add(callback);
  }

  removeObserver(callback) {
    this.observers.delete(callback);
  }

  notifyObservers(event, data) {
    this.observers.forEach(callback => callback(event, data));
  }
}

// Performance monitoring
class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }

  mark(name) {
    if (window.performance) {
      performance.mark(name);
    }
  }

  measure(name, startMark, endMark) {
    if (window.performance) {
      try {
        performance.measure(name, startMark, endMark);
        const measure = performance.getEntriesByName(name)[0];
        this.metrics.set(name, measure.duration);
      } catch (e) {
        console.warn('Performance measurement failed:', e);
      }
    }
  }

  getMetrics() {
    return Object.fromEntries(this.metrics);
  }
}

// Image optimization
class ImageOptimizer {
  static createObserver() {
    if (!window.IntersectionObserver) return null;
    
    return new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            this.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px'
    });
  }

  static optimizeImage(img, width = null) {
    if (!img.src) return;
    
    const url = new URL(img.src);
    if (width && url.hostname.includes('shopify')) {
      url.searchParams.set('width', width);
      img.src = url.toString();
    }
  }
}

// Module loading system
class ModuleLoader {
  constructor() {
    this.loadedModules = new Set();
    this.loadingModules = new Map();
  }

  async loadModule(name, path) {
    if (this.loadedModules.has(name)) {
      return Promise.resolve();
    }

    if (this.loadingModules.has(name)) {
      return this.loadingModules.get(name);
    }

    const loadPromise = ThemeUtils.loadScript(path).then(() => {
      this.loadedModules.add(name);
      this.loadingModules.delete(name);
    });

    this.loadingModules.set(name, loadPromise);
    return loadPromise;
  }
}

// Enhanced variant handling for high-variant products, combined listings, and auto-selection
class VariantManager {
  static isHighVariantProduct(variantCount) {
    return variantCount > 250;
  }

  static isCombinedListing(productUrl, optionValueProductUrl) {
    return optionValueProductUrl && productUrl !== optionValueProductUrl;
  }

  static buildOptionValuesUrl(baseUrl, optionValueIds) {
    const url = new URL(baseUrl, window.location.origin);
    if (optionValueIds && optionValueIds.length > 0) {
      url.searchParams.set('option_values', optionValueIds.join(','));
    }
    return url.toString();
  }

  static getFirstAvailableVariant(options) {
    // Find first available combination
    const availableValues = [];
    
    for (const option of options) {
      const availableValue = option.values.find(value => value.available);
      if (availableValue) {
        availableValues.push(availableValue);
      } else {
        return null; // No available variant possible
      }
    }
    
    return availableValues;
  }

  static buildVariantUrl(productUrl, selectedOptions) {
    const url = new URL(productUrl, window.location.origin);
    selectedOptions.forEach(option => {
      url.searchParams.set(option.name, option.value);
    });
    return url.toString();
  }

  static updateOptionAvailability(container, newData) {
    const optionSelectors = container.querySelectorAll('.option-value-selector');
    
    optionSelectors.forEach((selector, index) => {
      const newOption = newData.options[index];
      if (!newOption) return;
      
      const inputs = selector.querySelectorAll('input[type="radio"], option');
      inputs.forEach((input, inputIndex) => {
        const newValue = newOption.values[inputIndex];
        if (!newValue) return;
        
        // Update availability
        if (newValue.available) {
          input.disabled = false;
          input.classList.remove('disabled', 'option-value-unavailable');
          input.classList.add('option-value-available');
        } else {
          input.disabled = true;
          input.classList.add('disabled', 'option-value-unavailable');
          input.classList.remove('option-value-available');
        }
      });
    });
  }
}

// Initialize global instances
window.themeUtils = ThemeUtils;
window.cartManager = new CartManager();
window.performanceMonitor = new PerformanceMonitor();
window.moduleLoader = new ModuleLoader();
window.variantManager = VariantManager;

// Auto-initialize based on page context
document.addEventListener('DOMContentLoaded', () => {
  window.performanceMonitor.mark('theme-bundle-loaded');
  
  // Initialize image optimization
  const imageObserver = ImageOptimizer.createObserver();
  if (imageObserver) {
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  // Optimize existing images
  document.querySelectorAll('img[src*="shopify"]').forEach(img => {
    if (img.width && img.width < 800) {
      ImageOptimizer.optimizeImage(img, img.width * 2);
    }
  });
}); 